import { useState } from 'react';
import { TripsList } from '@/components/trips/TripsList';
import { TripCreationForm } from '@/components/trips/TripCreationForm';
import { ESIMRecommendations } from '@/components/logistics/ESIMRecommendations';
import { TransportSuggestions } from '@/components/logistics/TransportSuggestions';
import { ReceiptConverter } from '@/components/logistics/ReceiptConverter';
import { LocalTips } from '@/components/logistics/LocalTips';
import { FloatingChatButton } from '@/components/chat/FloatingChatButton';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Wifi, Car, Receipt } from 'lucide-react';

interface Trip {
  id: string;
  title: string;
  destination: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  trip_type: string;
  budget: number | null;
  currency: string;
  is_active: boolean;
}

export default function TripManagement() {
  const [currentView, setCurrentView] = useState<'list' | 'create' | 'manage'>('list');
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleTripCreated = () => {
    setCurrentView('list');
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSelectTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setCurrentView('manage');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedTrip(null);
  };

  if (currentView === 'create') {
    return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <Button 
            variant="ghost" 
            onClick={handleBackToList}
            className="flex items-center gap-2 hover:gap-3 transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Trips
          </Button>
        </div>
        <TripCreationForm onTripCreated={handleTripCreated} onCancel={handleBackToList} />
      </div>
      
      {/* Floating Chat Button */}
      <FloatingChatButton />
    </div>
    );
  }

  if (currentView === 'manage' && selectedTrip) {
    return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Button 
            variant="ghost" 
            onClick={handleBackToList}
            className="flex items-center gap-2 touch-manipulation self-start"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Trips
          </Button>
          <div className="text-left sm:text-right">
            <h1 className="text-xl sm:text-2xl font-bold">{selectedTrip.title}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {selectedTrip.destination}, {selectedTrip.destination_country}
            </p>
          </div>
        </div>

        <Tabs defaultValue="logistics" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-0 h-auto sm:h-10">
            <TabsTrigger value="logistics" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm touch-manipulation">
              <Wifi className="h-4 w-4 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Logistics</span>
              <span className="xs:hidden sm:hidden">eSIM</span>
            </TabsTrigger>
            <TabsTrigger value="transport" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm touch-manipulation">
              <Car className="h-4 w-4 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Transport</span>
              <span className="xs:hidden sm:hidden">Travel</span>
            </TabsTrigger>
            <TabsTrigger value="local-tips" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm touch-manipulation">
              <Receipt className="h-4 w-4 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Local Tips</span>
              <span className="xs:hidden sm:hidden">Tips</span>
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-3 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm touch-manipulation">
              <Receipt className="h-4 w-4 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline sm:inline">Expenses</span>
              <span className="xs:hidden sm:hidden">Bills</span>
            </TabsTrigger>
          </TabsList>

            <TabsContent value="logistics" className="mt-6">
              <div className="space-y-6">
                <ESIMRecommendations 
                  destination={selectedTrip.destination}
                  destinationCountry={selectedTrip.destination_country}
                />
              </div>
            </TabsContent>

            <TabsContent value="transport" className="mt-6">
              <div className="space-y-6">
                <TransportSuggestions 
                  destination={selectedTrip.destination}
                  destinationCountry={selectedTrip.destination_country}
                />
              </div>
            </TabsContent>

            <TabsContent value="local-tips" className="mt-6">
              <div className="space-y-6">
                <LocalTips 
                  destination={selectedTrip.destination}
                  destinationCountry={selectedTrip.destination_country}
                />
              </div>
            </TabsContent>

            <TabsContent value="expenses" className="mt-6">
              <div className="space-y-6">
                <ReceiptConverter tripId={selectedTrip.id} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      
      {/* Floating Chat Button */}
      <FloatingChatButton />
    </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <TripsList 
          onCreateTrip={() => setCurrentView('create')}
          onSelectTrip={handleSelectTrip}
          refreshTrigger={refreshTrigger}
        />
      </div>
      
      {/* Floating Chat Button */}
      <FloatingChatButton />
    </div>
  );
}